import React from "react";
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import produce from "immer";
import { initialState } from "./initialState";

export const colorVisionHeaders = ["Color Vision", "Eye", "Results"];

interface ColorVisionProps {
  visionData: typeof initialState;
  onVisionChange: (updatedVision: typeof initialState) => void;
}

const ROWS = ["Ishiara", "Lantern/Others"] as const;
const COLS = [
  { key: "eye", label: "Eye" },
  { key: "results", label: "Results" }
] as const;

export default function ColorVision({
  visionData,
  onVisionChange
}: ColorVisionProps): React.JSX.Element {
  const handleChange = (
    row: (typeof ROWS)[number],
    col: (typeof COLS)[number]["key"],
    value: string
  ) => {
    const updated = produce(visionData, (draft: typeof initialState) => {
      if (!draft.vision) {
        draft.vision = JSON.parse(JSON.stringify(initialState.vision));
      }
      const { colorVision } = draft.vision;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const eyeData = colorVision?.[row] as unknown as Record<string, any>;
      if (eyeData) {
        eyeData[col] = value === "" ? null : value;
      }
    });
    onVisionChange(updated);
  };

  const currentColorVision = visionData?.vision?.colorVision || {};

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <TableContainer>
      <Typography
        sx={{
          padding: 1,
          textAlign: "center",
          backgroundColor: "#ededed",
          borderLeft: 1,
          borderRight: 1,
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        Color Vision
      </Typography>

      <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellStyle, width: "150px" }} />
            {COLS.map((col) => (
              <TableCell key={col.key} sx={headerCellStyle}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ROWS.map((row) => (
            <TableRow key={row}>
              <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
              {COLS.map((col) => (
                <TableCell key={col.key} sx={cellStyle}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={currentColorVision[row]?.[col.key] ?? ""}
                    onChange={(e) => handleChange(row, col.key, e.target.value)}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                        fontSize: "0.875rem"
                      }
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
